/**
 * <s-offer-picker> — "Select a Product" picker for the Best Offers page.
 *
 * Every category card's Shop button ([data-offer-open]) opens the same dialog,
 * filtered to that category's rows ([data-offer-row][data-category]). The
 * shopper picks any number of units across flavors (one row per product);
 * the Buy 1 / Buy 2 / Buy 3+ strip highlights the tier for the units chosen
 * and the total uses that tier's per-unit price (display only: Kaching Bundles
 * applies the real discount in the cart).
 *
 * "Checkout" adds one cart line per chosen flavor through Shopify Standard
 * Actions, waits for the automatic free gift to settle and goes to checkout.
 * "Add to cart" adds the same lines and opens the cart drawer.
 *
 * Opener data: data-offer-open (category key), data-offer-title,
 * data-tier1/2/3 (cents per unit), data-view-all-url.
 * Host data: data-money-format, data-error-message, data-label-subtitle
 * ("[title]"), data-label-progress ("[count]"), data-label-view-all ("[title]").
 */
const MAX_PER_ROW = 20;

class SOfferPicker extends HTMLElement {
  #ac;
  #counts = new Map(); // variantId -> quantity
  #category = '';
  #tiers = [0, 0, 0];
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.dialog = this.querySelector('dialog');
    document.addEventListener('click', (event) => this.#onDocumentClick(event), opts);
    this.addEventListener('click', (event) => this.#onClick(event), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onDocumentClick(event) {
    const opener = event.target instanceof Element ? event.target.closest('[data-offer-open]') : null;
    if (!opener) return;
    const hasRows = this.querySelector(`[data-offer-row][data-category="${CSS.escape(opener.dataset.offerOpen || '')}"]`);
    if (!hasRows) return; // no rows for this category: let a link fallback navigate
    event.preventDefault();
    this.open(opener);
  }

  open(opener) {
    const category = opener.dataset.offerOpen || '';
    if (category !== this.#category) this.#counts.clear();
    this.#category = category;
    this.#tiers = ['tier1', 'tier2', 'tier3'].map((key) => Number.parseInt(opener.dataset[key] || '0', 10) || 0);
    const title = opener.dataset.offerTitle || '';

    const subtitle = this.querySelector('[data-offer-subtitle]');
    if (subtitle) subtitle.textContent = (this.dataset.labelSubtitle || '').replace('[title]', title);
    const viewAll = this.querySelector('[data-offer-view-all]');
    if (viewAll) {
      const url = opener.dataset.viewAllUrl || '';
      viewAll.hidden = !url;
      if (url) viewAll.setAttribute('href', url);
      viewAll.textContent = (this.dataset.labelViewAll || '').replace('[title]', title);
    }
    this.querySelectorAll('[data-offer-tier-price]').forEach((el, index) => {
      el.textContent = this.#tiers[index] ? this.#money(this.#tiers[index]) : '';
    });
    for (const row of this.querySelectorAll('[data-offer-row]')) {
      row.hidden = row.dataset.category !== category;
    }
    this.#showError('');
    this.#render();
    const list = this.querySelector('[data-offer-list]');
    if (list) list.scrollTop = 0;
    if (!this.dialog.open) this.dialog.showModal();
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const row = target.closest('[data-offer-row]');
    const key = row?.dataset.variantId;

    if (row && key && (target.closest('[data-offer-add]') || target.closest('[data-offer-plus]'))) {
      const next = Math.min(MAX_PER_ROW, (this.#counts.get(key) || 0) + 1);
      this.#counts.set(key, next);
      this.#render();
      if (target.closest('[data-offer-add]')) row.querySelector('[data-offer-plus]')?.focus();
    } else if (row && key && target.closest('[data-offer-minus]')) {
      const next = (this.#counts.get(key) || 0) - 1;
      if (next > 0) this.#counts.set(key, next);
      else this.#counts.delete(key);
      this.#render();
      if (next <= 0) row.querySelector('[data-offer-add]')?.focus();
    } else if (target.closest('[data-offer-checkout]')) {
      this.#proceed('checkout');
    } else if (target.closest('[data-offer-cart]')) {
      this.#proceed('cart');
    }
  }

  #selected() {
    let total = 0;
    for (const count of this.#counts.values()) total += count;
    return total;
  }

  #unitCents(quantity) {
    if (quantity >= 3) return this.#tiers[2];
    if (quantity === 2) return this.#tiers[1];
    return this.#tiers[0];
  }

  #money(cents) {
    const amount = (cents / 100).toFixed(2);
    const [whole, decimals] = amount.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const format = this.dataset.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
      if (kind === 'amount_no_decimals') return withCommas;
      if (kind === 'amount_with_comma_separator') return `${whole},${decimals}`;
      return `${withCommas}.${decimals}`;
    });
  }

  #root() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  async #proceed(mode) {
    const selected = this.#selected();
    if (this.#busy || selected < 1) return;
    const errorMessage = this.dataset.errorMessage || 'Could not add to cart.';
    if (!window.Shopify?.actions?.updateCart) {
      this.#showError(errorMessage);
      return;
    }
    const lines = [];
    for (const [variantId, quantity] of this.#counts.entries()) {
      const row = this.querySelector(`[data-offer-row][data-variant-id="${CSS.escape(variantId)}"]`);
      if (row && row.dataset.category === this.#category && quantity > 0) lines.push({ merchandiseId: variantId, quantity });
    }
    if (!lines.length) return;

    this.#busy = true;
    this.#setBusy(true);
    this.#showError('');
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines },
        { event: { context: 'product', detail: { source: 's-offer-picker', category: this.#category } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) {
        this.#showError(message);
        return;
      }
      this.#counts.clear();
      if (mode === 'checkout') {
        try {
          await window.TBFreeGift?.ensureSynced?.();
        } catch {
          /* checkout must not be blocked by the gift sync */
        }
        window.location.href = `${this.#root()}checkout`;
        return;
      }
      this.#render();
      if (this.dialog.open) this.dialog.close();
      await window.Shopify.actions.openCart?.();
    } catch (error) {
      this.#showError(error?.message || errorMessage);
    } finally {
      this.#busy = false;
      this.#setBusy(false);
    }
  }

  #setBusy(busy) {
    for (const button of this.querySelectorAll('[data-offer-checkout], [data-offer-cart]')) {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      button.classList.toggle('opacity-50', busy);
      button.classList.toggle('cursor-wait', busy);
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-offer-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  #render() {
    const selected = this.#selected();
    for (const row of this.querySelectorAll('[data-offer-row]')) {
      const count = this.#counts.get(row.dataset.variantId) || 0;
      row.querySelector('[data-offer-count]').textContent = String(count);
      row.querySelector('[data-offer-add]').hidden = count > 0;
      row.querySelector('[data-offer-stepper]').hidden = count === 0;
      row.querySelector('[data-offer-plus]').disabled = count >= MAX_PER_ROW;
      row.classList.toggle('ring-2', count > 0);
      row.classList.toggle('ring-interactive', count > 0);
    }

    const activeTier = selected >= 3 ? 2 : selected === 2 ? 1 : selected === 1 ? 0 : -1;
    this.querySelectorAll('[data-offer-tier]').forEach((el, index) => {
      const active = index === activeTier;
      el.classList.toggle('ring-2', active);
      el.classList.toggle('ring-interactive', active);
      el.classList.toggle('bg-interactive', active);
      el.classList.toggle('bg-surface', !active);
      if (active) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });

    const progress = this.querySelector('[data-offer-progress]');
    if (progress) progress.textContent = (this.dataset.labelProgress || '').replace('[count]', String(selected));
    const totalWrap = this.querySelector('[data-offer-total-wrap]');
    const totalEl = this.querySelector('[data-offer-total]');
    const unit = this.#unitCents(selected);
    if (totalWrap && totalEl) {
      totalWrap.hidden = !selected || !unit;
      totalEl.textContent = unit ? this.#money(unit * selected) : '';
    }
    for (const button of this.querySelectorAll('[data-offer-checkout], [data-offer-cart]')) {
      button.setAttribute('aria-disabled', String(selected < 1));
    }
  }
}

if (!customElements.get('s-offer-picker')) {
  customElements.define('s-offer-picker', SOfferPicker);
}
